/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util (booking request-payload builders + analytics side-effects)
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      moment-timezone; global `gtag`, `window.dataLayer`
 *   siblings:  booking-filters.js, booking-validation.js (other controllers/index.js extracts)
 *   tests:     booking-regressions-test.js (trackPurchase),
 *              classify-confirm-failure-test.js (+ withLocalizedLead), booking-price-and-outcome-test.js
 *   gotchas:   trackPurchase's `gtag` call is guarded (`typeof gtag ===
 *              'function'`) — it runs inside confirmBookingTask's try block
 *              before `bookConfirmed` is set, so an unguarded call on a page
 *              where the snippet is missing (blocked/stripped) would throw
 *              and be swallowed by the task's catch, leaving the user with
 *              neither a confirmation nor an error for a booking the server
 *              already accepted. Same guard as beebee-base/lib/router-tracking.js.
 *
 * Pure builders for the waiting-list and confirm-booking POST payloads,
 * trackPurchase() which fires the gtag/dataLayer purchase events for a
 * confirmed booking, and classifyConfirmFailure() which reads a rejected
 * confirm POST as refused / accepted / unknown; withLocalizedLead() puts a
 * localized line before raw server error details. No `this`: every input is an
 * explicit parameter.
 */
// Pure request-payload builders and analytics side-effects carved out of
// controllers/index.js. No `this`: every input is an explicit parameter.

import moment from 'moment-timezone';

const WAITING_LIST_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// Maps the positional monday..sunday checkbox flags onto the named day keys the
// waitinglist endpoint expects.
export function buildWaitingListPayload({
  me,
  productSelected,
  contactSelected,
  arrWaitListDays,
  waitListNotes,
}) {
  const days = WAITING_LIST_DAYS.reduce((acc, day, index) => {
    acc[day] = arrWaitListDays[index]?.value ?? false;
    return acc;
  }, {});

  return {
    ...(me?.id && { contact_id: me.id }),
    ...(productSelected?.id && { product_id: productSelected.id }),
    ...(contactSelected?.id && { specialist_id: contactSelected.id }),
    ...days,
    notes: waitListNotes ?? '',
  };
}

export function buildConfirmBookingPayload({
  me,
  people,
  voucher,
  voucherToBuy,
  contactSelected,
  productSelected,
  hourSelected,
  selectedTimeZone,
  organizationTermOptions,
  organizationName,
  thumbnailUrl,
  primaryColor,
  userId,
  lang,
}) {
  const address = me.addresses?.[0] || {};

  return {
    is_voucher: voucherToBuy ? true : false,
    voucher: voucher,
    contact_id: contactSelected?.id || null,
    contact_name: contactSelected?.name || null,
    contact_mail: contactSelected?.mail || null,
    product_id: productSelected?.id || null,
    product_name: productSelected?.name || null,
    first_name: me.first_name,
    last_name: me.last_name,
    password: me.password,
    piva: me.piva,
    ragione_sociale: me.ragione_sociale || '',
    fisica_giuridica: me.fisica_giuridica ? true : false,
    mobile: me.mobile,
    mail: me.mail,
    pec: me.pec,
    codice_destinatario: me.codice_destinatario,
    cod_fiscale: me.cod_fiscale,
    data_nascita: me.data_nascita,
    luogo_nascita: me.luogo_nascita,
    location_address_street: address.location_address_street || null,
    location_address_house: address.location_address_house || null,
    location_address_postal_code: address.location_address_postal_code || null,
    location_address_city: address.location_address_city || null,
    location_address_country: address.location_address_country || null,
    location_address_country_code: address.location_address_country_code || null,
    user_id: userId,
    event_group_id: voucherToBuy ? null : productSelected?.is_group ? hourSelected.id : null,
    from_date: voucherToBuy
      ? null
      : moment(hourSelected.start).tz(selectedTimeZone).format('YYYY-MM-DDTHH:mmZ'),
    to_date: voucherToBuy
      ? null
      : moment(hourSelected.end).tz(selectedTimeZone).format('YYYY-MM-DDTHH:mmZ'),
    stanza_id: voucherToBuy ? null : hourSelected.stanza_id || null,
    people: people,
    organization_term_options: organizationTermOptions,
    organization_name: organizationName,
    thumbnail_url: thumbnailUrl,
    primary_color: primaryColor,
    lang: lang,
  };
}

// Fires the gtag + dataLayer purchase events for a confirmed booking.
//
// The gtag call MUST stay guarded: `confirmBookingTask` calls this from inside
// its try block, before `bookConfirmed` is set, so an unguarded `gtag(...)` on
// a page where the snippet is missing (blocked by an extension, stripped by an
// embedding host) throws a ReferenceError that the task's catch swallows — the
// user gets neither a confirmation nor an error for a booking the server has
// already accepted. Same guard as beebee-base/lib/router-tracking.js.
export function trackPurchase(productSelected) {
  if (typeof gtag === 'function') {
    gtag('event', 'purchase', {
      event_category: 'ecommerce',
      event_label: productSelected,
      send_to: 'G-Q4R8QL4WC0',
    });
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        items: [
          {
            item_id: productSelected ? productSelected.id : null,
            item_name: productSelected ? productSelected.name : null,
            price: productSelected ? productSelected.price : null,
            quantity: productSelected ? productSelected.quantity || 1 : 1,
          },
        ],
      },
    });
  }
}

/**
 * Classifies a rejected POST /confirm_booking (or /booking_use_voucher) by what
 * it proves about the booking, which decides whether the patient may simply
 * press "confirm" again.
 *
 * - 'accepted'  the server COMMITTED the booking and only its follow-up failed:
 *               api_beebeeboard answers `422 {id:0}` (no `errors`) when the
 *               confirmation mail call to the asset server errors, after
 *               booking_confirm_v1 already created the event. Retrying would
 *               book twice (or read "Non disponibile" for the patient's own slot).
 * - 'refused'   a 4xx from the handler or a proxy: the request was rejected
 *               before any write (404/422/403 mapped to 422 with `errors`, a
 *               rolled-back SP error as 400, 408/413/429). Retrying is safe.
 * - 'unknown'   no answer (status 0: network drop, client or gateway timeout)
 *               or a 5xx: the request may have been committed before the
 *               failure. The patient must check mail / Area personale first.
 *
 * Frontend mitigation only: the server should answer 2xx for a committed
 * booking and accept an idempotency key (POP-428).
 *
 * `err` is the Error thrown by beebeeAjaxPost (`status`, `responseJSON`).
 */
export function classifyConfirmFailure(err) {
  const body = err?.responseJSON;
  const status = Number(err?.status) || 0;

  const committedId = body && !Array.isArray(body.errors) && body.id !== undefined && body.id !== null;
  if (committedId && String(body.id) === '0') {
    return 'accepted';
  }
  // The same notify failure on booking_use_voucher answers 422 {id:<n>}:
  // written, but not a shape the page can open — never a plain retry.
  if (committedId || !(status >= 400 && status < 500)) {
    return 'unknown';
  }
  return 'refused';
}

/**
 * What the booking page shows for a rejected confirm POST:
 * `{ outcome, bookError, userExists }`. `bookError` is set only for a refusal
 * (the server's own errors, or `genericMessage` when the 4xx has no body).
 */
export function confirmFailureView(err, genericMessage) {
  const outcome = classifyConfirmFailure(err);
  if (outcome === 'accepted') {
    // Same shape as the success reply for a booking with nothing to pay.
    return { outcome, id: 0, bookError: null, userExists: false };
  }
  if (outcome === 'unknown') {
    return { outcome, bookError: null, userExists: false };
  }
  const errors = err?.responseJSON?.errors;
  if (errors?.[0]?.title === 'Utente già esistente') {
    return { outcome, bookError: null, userExists: true };
  }
  return {
    outcome,
    bookError: Array.isArray(errors) && errors.length ? err.responseJSON : { errors: [{ detail: genericMessage }] },
    userExists: false,
  };
}

// A refusal that carries the SERVER's errors shows their `detail` verbatim, and
// that text is whatever the API wrote (often Italian, often technical: «Parametro
// non valido»). It is never shown alone: a localized lead line goes first, so a
// tourist reading en/de/fr knows the booking was not made and what to do. The
// generic message (no server errors) is already localized: left as it is.
export function withLocalizedLead(bookError, genericMessage, lead) {
  const errors = bookError?.errors;
  if (!Array.isArray(errors) || !errors.length || !lead) return bookError;
  if (errors.length === 1 && errors[0]?.detail === genericMessage) return bookError;
  return { ...bookError, errors: [{ detail: lead }, ...errors] };
}
